import { type ButtonHTMLAttributes, createElement, type HTMLAttributes, type InputHTMLAttributes, type ReactElement, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx, gapAttr, type GapStep } from './library-kit';

/**
 * D550 (ADMIN-A): small structural pieces the Admin shell and the Admin listing screens needed and the D548 vocabulary does not
 * name. Same rule as the vocabulary: no `style`, no length — every option is a token name or a boolean.
 */

type BlockAttrs = Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'>;

// ---- PopoverAnchor ---------------------------------------------------------------------------------------------------------------

export interface PopoverAnchorProps extends BlockAttrs {
  /** Do not let a flex parent shrink or grow it (the toolbar's menu buttons). */
  readonly fixed?: boolean;
  readonly children?: ReactNode;
}

/** The box a `Popover` positions itself against (`position: relative`): wraps the trigger button and the popover. */
export function PopoverAnchor({ fixed = false, children, style: _style, ...rest }: PopoverAnchorProps & { style?: unknown }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-anchor', 'data-fixed': fixed ? 'true' : undefined }, children);
}

// ---- Toolbar ---------------------------------------------------------------------------------------------------------------------

export interface ToolbarProps extends BlockAttrs {
  readonly children?: ReactNode;
}

/** The list screen's toolbar row (A08): search, segmented filter and menu buttons on one line, gap 10, wrapping on a narrow window. */
export function Toolbar({ children, style: _style, ...rest }: ToolbarProps & { style?: unknown }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-toolbar' }, children);
}

// ---- Scope menu layout -------------------------------------------------------------------------------------------------------------

export interface ScopeColumnsProps extends BlockAttrs {
  /**
   * How many levels the menu shows side by side (parent levels | ... | the deepest level): 1, 2 (countries | cities) or 3
   * (countries | states | cities). Generic - the component does not know what the levels are.
   */
  readonly columns?: 1 | 2 | 3;
  /** Legacy switch for the two-level layout: `true` = 2 columns, `false` = 1. `columns` wins when both are given. */
  readonly twoColumns?: boolean;
  readonly children?: ReactNode;
}

/** The scope switcher's menu body: the A08 multi-column layout (parent levels 200px each | the deepest level) or a single column. */
export function ScopeColumns({ columns, twoColumns = false, children, style: _style, ...rest }: ScopeColumnsProps & { style?: unknown }): ReactElement {
  const count = columns ?? (twoColumns ? 2 : 1);
  return createElement('div', { ...rest, className: 'vello-admin-a-scope', 'data-columns': String(count) }, children);
}

export interface ScopeColumnProps extends BlockAttrs {
  /** A hairline on the inline end (every column that another column follows). */
  readonly divided?: boolean;
  readonly children?: ReactNode;
}

/** One column of the scope menu: the heading and its items stacked with the menu's own tight gap. */
export function ScopeColumn({ divided = false, children, style: _style, ...rest }: ScopeColumnProps & { style?: unknown }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-scope__col', 'data-divided': divided ? 'true' : undefined }, children);
}

// ---- Box -------------------------------------------------------------------------------------------------------------------------

export interface BoxProps extends BlockAttrs {
  /** The space step between the children. Default 3 (12px). */
  readonly gap?: GapStep;
  /** The inner padding as a space step. Default 4 (16px). */
  readonly pad?: 2 | 3 | 4 | 5 | 6;
  /** `default` (a framed group inside a panel), `subtle` (a soft surface), `flush` (a frame whose children carry their own edges). */
  readonly tone?: 'default' | 'subtle' | 'flush' | 'ruled';
  /** `row`: the children sit on one wrapping line (a saved filter: its name, then its actions). */
  readonly direction?: 'column' | 'row';
  readonly as?: 'div' | 'section' | 'li' | 'article';
  readonly children?: ReactNode;
}

/** A framed group inside a panel: 1px hairline, card radius, padded, children stacked. Replaces the hand-drawn `border + radius + padding` boxes. */
export function Box({ gap, pad, tone = 'default', direction = 'column', as = 'div', className, children, style: _style, ...rest }: BoxProps & { className?: string; style?: unknown }): ReactElement {
  return createElement(
    as,
    { ...rest, className: cx('vello-admin-a-box', className), 'data-gap': gapAttr(gap), 'data-pad': pad === undefined ? undefined : String(pad), 'data-tone': tone === 'default' ? undefined : tone, 'data-dir': direction === 'row' ? 'row' : undefined },
    children,
  );
}

// ---- ChoiceGroup -----------------------------------------------------------------------------------------------------------------

export interface ChoiceGroupProps extends Omit<HTMLAttributes<HTMLFieldSetElement>, 'style' | 'className' | 'children'> {
  readonly children?: ReactNode;
}

/** A `<fieldset>` of options (radios, checks) on one wrapping line: the legend first, then the choices, no browser frame. */
export function ChoiceGroup({ children, style: _style, ...rest }: ChoiceGroupProps & { style?: unknown }): ReactElement {
  return createElement('fieldset', { ...rest, className: 'vello-admin-a-choices' }, children);
}

// ---- RecordPanel / RecordRow / RecordSection ---------------------------------------------------------------------------------------

export interface RecordPanelProps extends Omit<BlockAttrs, 'title'> {
  /** The panel's title (the head, label size / 700). */
  readonly title: ReactNode;
  /** Controls or a status on the right of the head. */
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

/**
 * A section of a client record page (the A08 ficha panel): a hairline frame at the card radius, a head with the title and its
 * status/actions, and a padded body whose blocks are stacked. Replaces the hand-drawn PANEL / HEAD / BODY triple of the person panels.
 */
export function RecordPanel({ title, actions, children, style: _style, ...rest }: RecordPanelProps & { style?: unknown }): ReactElement {
  return (
    <div {...rest} className="vello-admin-a-record">
      <div className="vello-admin-a-record__head">
        <span>{title}</span>
        {actions !== undefined && (
          <>
            <span className="vello-grow" aria-hidden="true" />
            {actions}
          </>
        )}
      </div>
      <div className="vello-admin-a-record__body">{children}</div>
    </div>
  );
}

export interface RecordRowProps extends BlockAttrs {
  /** No minimum height (a key/value line rather than a row with a button). */
  readonly dense?: boolean;
  /** The row pads and gaps its label tighter (a label wrapping its own field). */
  readonly tight?: boolean;
  readonly as?: 'div' | 'label';
  readonly children?: ReactNode;
}

/** A line of a record section: items on one wrapping row, caption size, 48px tall unless `dense`. */
export function RecordRow({ dense = false, tight = false, as = 'div', children, style: _style, ...rest }: RecordRowProps & { style?: unknown }): ReactElement {
  return createElement(as, { ...rest, className: 'vello-admin-a-line', 'data-dense': dense ? 'true' : undefined, 'data-tight': tight ? 'true' : undefined }, children);
}

/** A record row that is itself a control (opens the record): full width, 48px, a hover wash, the row's own focus ring. */
export function RecordRowButton({ children, style: _style, type = 'button', ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'className' | 'children'> & { style?: unknown; children?: ReactNode }): ReactElement {
  return (
    <button {...rest} type={type} className="vello-admin-a-linebtn">
      {children}
    </button>
  );
}

/** A sub-block of a record panel under a hairline: its own title and lines. */
export function RecordSection({ children, style: _style, ...rest }: BlockAttrs & { style?: unknown; children?: ReactNode }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-section' }, children);
}

// ---- EmptyCell, Measure, ScrollX --------------------------------------------------------------------------------------------------

/** The empty (or loading) message inside a list: centred, tertiary ink, padded. The `empty` slot of a `ListTable`/`Table`. */
export function EmptyCell({ children, style: _style, ...rest }: BlockAttrs & { style?: unknown; children?: ReactNode }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-emptycell' }, children);
}

export interface MeasureProps extends BlockAttrs {
  /** The reading width: `sm` 24rem (a short form), `md` 34rem (a form), `lg` 45rem (a long form). */
  readonly size: 'sm' | 'md' | 'lg';
  /** The space step between the children. Default 4 (16px). */
  readonly gap?: GapStep;
  readonly as?: 'div' | 'section' | 'fieldset';
  readonly children?: ReactNode;
}

/** A column of blocks held to a readable width (forms and long text do not stretch across a wide page). */
export function Measure({ size, gap, as = 'div', children, style: _style, ...rest }: MeasureProps & { style?: unknown }): ReactElement {
  return createElement(as, { ...rest, className: 'vello-stack vello-admin-a-measure', 'data-size': size, 'data-gap': gapAttr(gap) }, children);
}

export interface ScrollXProps extends BlockAttrs {
  /** Keep the content at least this wide (`36rem`) and scroll sideways below it. */
  readonly wide?: boolean;
  readonly children?: ReactNode;
}

/** A box that scrolls sideways on its own when its content is wider than the page (a chart's table), never the page. */
export function ScrollX({ wide = false, children, style: _style, ...rest }: ScrollXProps & { style?: unknown }): ReactElement {
  return createElement('div', { ...rest, className: 'vello-admin-a-scrollx' }, wide ? createElement('div', { className: 'vello-admin-a-scrollx__inner' }, children) : children);
}

// ---- Divided, SectionHead ----------------------------------------------------------------------------------------------------------

export interface DividedProps extends BlockAttrs {
  /** The space step between the children and under the hairline. Default 4 (16px). */
  readonly gap?: GapStep;
  readonly as?: 'div' | 'section';
  readonly children?: ReactNode;
}

/** A block under a hairline (a form slot after the previous one, a member row): padding-top, children stacked. */
export function Divided({ gap, as = 'div', children, style: _style, ...rest }: DividedProps & { style?: unknown }): ReactElement {
  return createElement(as, { ...rest, className: 'vello-stack vello-admin-a-divided', 'data-gap': gapAttr(gap) }, children);
}

export interface SectionHeadProps {
  readonly title: ReactNode;
  /** A line under the title (what the section is for). */
  readonly subtitle?: ReactNode;
  /** Controls on the right. */
  readonly actions?: ReactNode;
}

/** The head of a flush panel with a subtitle: the `PanelHead` anatomy (title left, actions right, hairline) plus the line under the title. */
export function SectionHead({ title, subtitle, actions }: SectionHeadProps): ReactElement {
  return (
    <header className="vello-panelhead">
      <div className="vello-admin-a-sectionhead__text">
        <h2 className="vello-panelhead__title">{title}</h2>
        {subtitle !== undefined && <p className="vello-admin-a-sectionhead__sub">{subtitle}</p>}
      </div>
      {actions !== undefined && <div className="vello-panelhead__actions">{actions}</div>}
    </header>
  );
}

// ---- ChipFrame / ChipMain -----------------------------------------------------------------------------------------------------------

/** A saved condition as a pill: the frame holds the main button and the remove control. */
export function ChipFrame({ children, style: _style, ...rest }: BlockAttrs & { style?: unknown; children?: ReactNode }): ReactElement {
  return createElement('li', { ...rest, className: 'vello-admin-a-chip' }, children);
}

/** The pill's main button (opens the editor). It carries the 48px floor in the stylesheet. */
export function ChipMain({ children, style: _style, type = 'button', ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'className' | 'children'> & { style?: unknown; children?: ReactNode }): ReactElement {
  return (
    <button {...rest} type={type} className="vello-admin-a-chip__main">
      {children}
    </button>
  );
}

// ---- MonoArea ---------------------------------------------------------------------------------------------------------------------

/** A multi-line control for data typed as code (the JSON maps of a gateway template): mono type, the field surface, vertical resize. */
export function MonoArea({ compact = false, style: _style, ...rest }: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'style' | 'className'> & { compact?: boolean; style?: unknown }): ReactElement {
  return <textarea spellCheck={false} {...rest} className="vello-input vello-admin-a-monoarea" data-compact={compact ? 'true' : undefined} />;
}

// ---- Line ---------------------------------------------------------------------------------------------------------------------------

export interface LineProps extends BlockAttrs {
  /** Vertical padding step (2 = 8px, 3 = 12px). Default 2. */
  readonly pad?: 2 | 3;
  /** A hairline under the line (a list of lines). */
  readonly ruled?: boolean;
  /** Caption size for the whole line. */
  readonly caption?: boolean;
  /** Let the two sides wrap under each other on a narrow width. */
  readonly wrap?: boolean;
  /** Gap step between the sides: 2 or 3. Default 3. */
  readonly gap?: 2 | 3;
  readonly children?: ReactNode;
}

/** One line of a list or a definition list: its two sides at the ends (name left, value right), padded, optionally ruled. */
export function Line({ pad = 2, ruled = false, caption = false, wrap = false, gap = 3, children, style: _style, ...rest }: LineProps & { style?: unknown }): ReactElement {
  return createElement(
    'div',
    { ...rest, className: 'vello-admin-a-line-kv', 'data-pad': pad === 3 ? '3' : undefined, 'data-ruled': ruled ? 'true' : undefined, 'data-caption': caption ? 'true' : undefined, 'data-wrap': wrap ? 'true' : undefined, 'data-gap': gap === 2 ? '2' : undefined },
    children,
  );
}

// ---- MonoInput ----------------------------------------------------------------------------------------------------------------------

/** A single-line control for data typed as code (an id, a handle): mono type at caption size on the field surface. */
export function MonoInput({ style: _style, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'style' | 'className'> & { style?: unknown }): ReactElement {
  return <input {...rest} className="vello-input vello-admin-a-monoinput" />;
}
